import { useState } from 'react'
import { Modal, ActionIcon, Tooltip, Loader } from '@mantine/core'
import { ClockRewind } from '@untitled-ui/icons-react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { useAxiosApi } from '@/hooks/useAxiosApi'

// ─── Date helpers ─────────────────────────────────────────────────────────────

const formatTimestamp = (date) => dayjs(date).format('HH:mm')

const groupLabel = (dateStr) => {
    const d = dayjs(dateStr)
    const now = dayjs()
    const diffDays = now.startOf('day').diff(d.startOf('day'), 'day')

    if (diffDays === 0) return "Aujourd'hui"
    if (diffDays === 1) return 'Hier'
    if (diffDays < 7) return d.format('dddd D MMMM')
    if (d.year() === now.year()) return d.format('D MMMM')
    return d.format('D MMM YYYY')
}

const groupKey = (date) => dayjs(date).format('YYYY-MM-DD')

// ─── Chip ─────────────────────────────────────────────────────────────────────

const Chip = ({ field, value, formatChip, muted }) => {
    const base = 'inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium leading-tight'
    const defaultStyle = muted ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-600'

    if (value === null || value === undefined || value === '') {
        return <span className={`${base} ${defaultStyle}`}>—</span>
    }

    const custom = formatChip?.(field, value)
    const label = custom?.label
        ?? (typeof value === 'boolean' || value === '1' || value === '0'
            ? (value === true || value === 1 || value === '1' ? 'Oui' : 'Non')
            : String(value))

    return (
        <span className={`${base} ${custom?.className || defaultStyle}`}>
            {label}
        </span>
    )
}

// ─── Entry rendering ──────────────────────────────────────────────────────────

const defaultFieldLabels = {
    status: 'Statut',
    type: 'Type',
    // Purchase orders
    reference: 'Référence',
    name: 'Nom',
    supplier_id: 'Fournisseur',
    location_id: 'Emplacement',
    canceled_at: 'Annulé le',
    closed_at: 'Fermé le',
    quantity_to_receive: 'Quantité à recevoir',
    quantity_received: 'Quantité reçue',
    reception_at: 'Réception le',
    // Fulfillment orders
    fulfill_at: 'Date de préparation',
    to_location_id: 'Destination',
    warranties_mode: 'Mode garanties',
    fulfillment_mode: 'Mode de préparation',
    incoterm: 'Incoterm',
    customs_mode: 'Mode douane',
    is_being_prepared: 'En cours de préparation',
    consignee: 'Destinataire',
    // Production orders
    starting_at: 'Début',
    ending_at: 'Fin',
    quantity: 'Quantité',
    quantity_completed: 'Quantité terminée',
    quantity_in_control: 'Quantité en contrôle',
    references: 'Références',
    // Components
    value: 'Valeur',
    value_currency: 'Devise',
    inventory_cycle_days: 'Cycle d\u2019inventaire (j)',
    warranty_visible_by_all_retailers: 'Garantie visible par tous les revendeurs',
    warranty_visible_on_website_contact: 'Garantie visible sur le site',
    watch_guide_url: 'Guide d’utilisation',
    is_keep_in_stock: 'Gardé en stock',
    dispatch_date: 'Date de dispatch',
    availability_b2b: 'Disponibilité B2B',
    availability_b2c: 'Disponibilité B2C',
    category: 'Catégorie',
    // Retailers
    default_markup_rate: 'Taux de marge',
    public_id: 'Identifiant public',
    has_free_shipping: 'Livraison offerte',
    shipping_address: 'Adresse de livraison',
    is_priority: 'Prioritaire',
    is_handled_by: 'Lieu de traitement',
    is_archived: 'Archivé',
    product_reference: 'Référence produit',
    component_id: 'Composant',
    order_id: 'Commande',
    customer_name: 'Nom client',
    customer_email: 'Email client',
    customer_phone: 'Téléphone client',
    customer_locale: 'Langue client',
    customer_reason: 'Motif client',
    // Priorisation des demandes
    title: 'Titre',
    summary: 'Résumé',
    requester_id: 'Demandeur',
    owner_id: 'Cadre responsable',
    source_url: 'Permalien',
    requested_at: 'Date de demande',
}

const FieldChange = ({ field, oldVal, newVal, formatChip }) => (
    <div className="flex flex-col gap-1.5">
        <span className="text-[10px] text-gray-400 uppercase tracking-wide leading-none">
            {defaultFieldLabels[field] || field}
        </span>
        <div className="flex items-center gap-1.5">
            <Chip field={field} value={oldVal} formatChip={formatChip} muted />
            <span className="text-xs text-gray-300">→</span>
            <Chip field={field} value={newVal} formatChip={formatChip} />
        </div>
    </div>
)

const ActivityEntry = ({ activity, formatChip, getEntryLabel }) => {
    const changes = activity.attribute_changes
    const time = formatTimestamp(activity.created_at)

    const overrideLabel = getEntryLabel?.(activity)
    const isCreated = activity.event === 'created'
    const hasChanges = !overrideLabel && !isCreated && changes?.attributes
    const simpleLabel = overrideLabel ?? (isCreated ? 'Création' : (!hasChanges ? activity.event : null))

    return (
        <div className="relative pl-6 pb-5 last:pb-0">
            <div className="absolute left-0 top-[6px] w-2 h-2 rounded-full bg-gray-300 -translate-x-1/2 ring-2 ring-white" />

            <div className="flex items-start justify-between gap-4 min-w-0">
                <div className="flex flex-col gap-2 min-w-0">
                    {activity.label && (
                        <span className="text-xs font-medium text-gray-500">
                            {activity.label}
                        </span>
                    )}
                    {simpleLabel && (
                        <span className="text-sm text-gray-700">{simpleLabel}</span>
                    )}

                    {hasChanges && Object.keys(changes.attributes).map((field) => (
                        <FieldChange
                            key={field}
                            field={field}
                            oldVal={changes.old?.[field]}
                            newVal={changes.attributes[field]}
                            formatChip={formatChip}
                        />
                    ))}

                    {activity.causer && (
                        <span className="text-xs text-gray-400">{activity.causer.name}</span>
                    )}
                </div>

                <span className="text-xs text-gray-400 shrink-0 mt-0.5 tabular-nums">
                    {time}
                </span>
            </div>
        </div>
    )
}

const DateGroup = ({ label, activities, formatChip, getEntryLabel }) => (
    <div className="mb-6 last:mb-0">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            {label}
        </div>
        <div className="relative border-l border-gray-200 ml-1">
            {activities.map((activity) => (
                <ActivityEntry
                    key={activity.id}
                    activity={activity}
                    formatChip={formatChip}
                    getEntryLabel={getEntryLabel}
                />
            ))}
        </div>
    </div>
)

// ─── Main component ────────────────────────────────────────────────────────────

/**
 * Generic activity log button + modal.
 *
 * Props:
 * - subjectType  slug used by ActivityLogController (e.g. "service-cases")
 * - subjectId    numeric ID of the record
 * - formatChip   optional fn(field, value) => { label, className? } | undefined
 */
export default function ActivityLog({ subjectType, subjectId, formatChip, getEntryLabel }) {
    const [isOpen, setIsOpen] = useState(false)
    const { requestGet } = useAxiosApi()

    const { data: response, isLoading } = useSWR(
        isOpen ? `/activity-log/${subjectType}/${subjectId}` : null,
        requestGet
    )
    const activities = response?.data || []

    const groups = activities.reduce((acc, activity) => {
        const key = groupKey(activity.created_at)
        if (!acc[key]) acc[key] = []
        acc[key].push(activity)
        return acc
    }, {})

    return (
        <>
            <Tooltip label="Historique">
                <ActionIcon variant="default" size={36} onClick={() => setIsOpen(true)}>
                    <ClockRewind width={16} height={16} />
                </ActionIcon>
            </Tooltip>

            <Modal
                opened={isOpen}
                onClose={() => setIsOpen(false)}
                title="Historique"
                size="lg"
                centered
                styles={{ body: { paddingTop: 8 } }}
            >
                {isLoading ? (
                    <div className="flex justify-center py-10">
                        <Loader size="sm" />
                    </div>
                ) : activities.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Aucun événement</p>
                ) : (
                    <div className="max-h-[60vh] overflow-y-auto pr-1">
                        {Object.entries(groups).map(([key, items]) => (
                            <DateGroup
                                key={key}
                                label={groupLabel(key)}
                                activities={items}
                                formatChip={formatChip}
                                getEntryLabel={getEntryLabel}
                            />
                        ))}
                    </div>
                )}
            </Modal>
        </>
    )
}
